import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Layout/Header';
import {
  analyticsService,
  AnalyticsDashboardResponse,
} from '../services/analytics.service';
import './AdminAnalytics.css';

const formatNumber = (value: number) => value.toLocaleString();
const formatPercent = (value: number) => `${value.toFixed(2)}%`;

const SECTIONS = [
  { id: 'section-users', label: 'Users' },
  { id: 'section-engagement', label: 'Engagement' },
  { id: 'section-policies', label: 'Policies' },
  { id: 'section-notifications', label: 'Notifications' },
  { id: 'section-methodology', label: 'How metrics are calculated' },
] as const;

const AdminAnalytics: React.FC = () => {
  const [data, setData] = useState<AnalyticsDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const analytics = await analyticsService.getDashboard();
        setData(analytics);
      } catch (err: any) {
        setError(err?.message || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const peakAcceptanceTrend = useMemo(
    () => Math.max(...(data?.policies.acceptanceTrend.map((p) => p.count) || [1])),
    [data?.policies.acceptanceTrend],
  );

  return (
    <div className="admin-analytics-page">
      <Header />
      <div className="admin-analytics-container">
        <div className="analytics-heading">
          <h1>Admin Analytics Dashboard</h1>
          <p>
            Operational metrics grouped by area. Use the links to jump; see{' '}
            <strong>How metrics are calculated</strong> for formulas.{' '}
            <Link to="/admin/leads" className="analytics-heading-link">
              Vehicle Leads
            </Link>
          </p>
        </div>

        {loading && <div className="analytics-state">Loading analytics...</div>}
        {error && <div className="analytics-state analytics-error">{error}</div>}

        {!loading && !error && data && (
          <>
            <nav className="analytics-section-nav" aria-label="Analytics sections">
              {SECTIONS.map((s) => (
                <a key={s.id} className="analytics-section-nav-link" href={`#${s.id}`}>
                  {s.label}
                </a>
              ))}
            </nav>

            {/* ── Users ── */}
            <section id="section-users" className="analytics-section">
              <header className="analytics-section-header">
                <h2 className="analytics-section-title">Users</h2>
              </header>
              <div className="analytics-cards analytics-cards--section">
                <div className="analytics-card">
                  <span>Total employees</span>
                  <strong>{formatNumber(data.users.totalEmployees)}</strong>
                </div>
                <div className="analytics-card">
                  <span>Profile completion (email + photo)</span>
                  <strong>{formatPercent(data.users.profileCompletionRate)}</strong>
                </div>
              </div>
              <div className="analytics-grid analytics-grid--section">
                <div className="analytics-panel">
                  <h3 className="analytics-panel-title">Role distribution</h3>
                  <p className="analytics-panel-desc">
                    Count of users per role (EMPLOYEE, ADMIN, Co-Admin, Content uploader). Bar length is the share of all users.
                  </p>
                  {data.users.roleDistribution.map((item) => (
                    <div className="bar-row" key={item.role}>
                      <div className="bar-label">{item.role}</div>
                      <div className="bar-track">
                        <div
                          className="bar-fill bar-fill--users"
                          style={{
                            width: `${
                              data.users.totalEmployees > 0
                                ? (item.count / data.users.totalEmployees) * 100
                                : 0
                            }%`,
                          }}
                        />
                      </div>
                      <div className="bar-value">{formatNumber(item.count)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* ── Engagement ── */}
            <section id="section-engagement" className="analytics-section">
              <header className="analytics-section-header">
                <h2 className="analytics-section-title">Engagement</h2>
              </header>
              <div className="analytics-cards analytics-cards--section">
                <div className="analytics-card">
                  <span>Total posts</span>
                  <strong>{formatNumber(data.posts.totalPosts)}</strong>
                </div>
              </div>
              <div className="analytics-grid analytics-grid--section">
                <div className="analytics-panel">
                  <h3 className="analytics-panel-title">Posts by category</h3>
                  <p className="analytics-panel-desc">
                    Number of posts per <code>category</code> field. Bar length is the share of all posts.
                  </p>
                  {data.posts.postsByCategory.map((item) => (
                    <div className="bar-row" key={item.category}>
                      <div className="bar-label">{item.category}</div>
                      <div className="bar-track">
                        <div
                          className="bar-fill bar-fill--engagement"
                          style={{
                            width: `${
                              data.posts.totalPosts > 0 ? (item.count / data.posts.totalPosts) * 100 : 0
                            }%`,
                          }}
                        />
                      </div>
                      <div className="bar-value">{item.count}</div>
                    </div>
                  ))}
                </div>

                <div className="analytics-panel">
                  <h3 className="analytics-panel-title">Posts by department</h3>
                  <p className="analytics-panel-desc">
                    Uses each post&apos;s <code>department</code> tag; blank values are grouped as
                    UNSPECIFIED.
                  </p>
                  {data.posts.postsByDepartment.map((item) => (
                    <div className="bar-row" key={item.department}>
                      <div className="bar-label">{item.department}</div>
                      <div className="bar-track">
                        <div
                          className="bar-fill bar-fill--engagement"
                          style={{
                            width: `${
                              data.posts.totalPosts > 0 ? (item.count / data.posts.totalPosts) * 100 : 0
                            }%`,
                          }}
                        />
                      </div>
                      <div className="bar-value">{item.count}</div>
                    </div>
                  ))}
                </div>

                <div className="analytics-panel">
                  <h3 className="analytics-panel-title">Most liked posts (top 10)</h3>
                  <p className="analytics-panel-desc">Posts ranked by total like count (all-time).</p>
                  <div className="rank-list">
                    {data.posts.mostLikedPosts.map((post) => (
                      <div className="rank-row" key={post.postId}>
                        <div>
                          <strong>{post.caption}</strong>
                          <p>
                            {post.category} {post.department ? `• ${post.department}` : ''} • {post.createdByName}
                          </p>
                        </div>
                        <span>{post.likeCount} likes</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="analytics-panel">
                  <h3 className="analytics-panel-title">Most commented posts (top 10)</h3>
                  <p className="analytics-panel-desc">Posts ranked by total comment count (all-time).</p>
                  <div className="rank-list">
                    {data.posts.mostCommentedPosts.map((post) => (
                      <div className="rank-row" key={post.postId}>
                        <div>
                          <strong>{post.caption}</strong>
                          <p>
                            {post.category} {post.department ? `• ${post.department}` : ''} • {post.createdByName}
                          </p>
                        </div>
                        <span>{post.commentCount} comments</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="analytics-panel">
                  <h3 className="analytics-panel-title">Most active users</h3>
                  <p className="analytics-panel-desc">
                    Each user&apos;s activity score = posts created + likes given + comments made (lifetime).
                    Listed users are sorted by this sum, top 10.
                  </p>
                  <div className="rank-list">
                    {data.posts.mostActiveUsers.map((user) => (
                      <div className="rank-row" key={user.userId}>
                        <div>
                          <strong>{user.fullName}</strong>
                          <p>
                            {user.role} • {user.department}
                          </p>
                        </div>
                        <span>{user.totalActivity} actions</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* ── Policies ── */}
            <section id="section-policies" className="analytics-section">
              <header className="analytics-section-header">
                <h2 className="analytics-section-title">Policies</h2>
              </header>
              <div className="analytics-cards analytics-cards--section">
                <div className="analytics-card">
                  <span>Active policies</span>
                  <strong>{formatNumber(data.policies.totalActivePolicies)}</strong>
                </div>
                <div className="analytics-card">
                  <span>Requiring acceptance</span>
                  <strong>{formatNumber(data.policies.policiesRequiringAcceptance)}</strong>
                </div>
                <div className="analytics-card">
                  <span>Users fully compliant</span>
                  <strong>
                    {formatNumber(data.policies.usersWithAllPoliciesAcceptedVsPending.usersWithAllPoliciesAccepted)}
                  </strong>
                </div>
                <div className="analytics-card">
                  <span>Users with pending acceptances</span>
                  <strong>
                    {formatNumber(data.policies.usersWithAllPoliciesAcceptedVsPending.usersWithPendingPolicies)}
                  </strong>
                </div>
              </div>
              <div className="analytics-grid analytics-grid--section">
                <div className="analytics-panel">
                  <h3 className="analytics-panel-title">Acceptance rate per policy</h3>
                  <p className="analytics-panel-desc">
                    For each active policy that requires acceptance: accepted users ÷ total employees × 100.
                    Counts come from policy acceptance records.
                  </p>
                  {data.policies.acceptanceRatePerPolicy.map((policy) => (
                    <div className="bar-row" key={policy.policyId}>
                      <div className="bar-label">{policy.title}</div>
                      <div className="bar-track">
                        <div className="bar-fill bar-fill--policies" style={{ width: `${policy.acceptanceRate}%` }} />
                      </div>
                      <div className="bar-value">{formatPercent(policy.acceptanceRate)}</div>
                    </div>
                  ))}
                </div>

                <div className="analytics-panel">
                  <h3 className="analytics-panel-title">Acceptance trend over time</h3>
                  <p className="analytics-panel-desc">
                    Number of policy acceptances recorded per calendar month (by <code>acceptedAt</code>
                    timestamp).
                  </p>
                  <div className="trend-list">
                    {data.policies.acceptanceTrend.map((point) => (
                      <div className="trend-point" key={point.period}>
                        <span>{point.period}</span>
                        <div className="trend-line">
                          <div
                            className="trend-line-fill trend-line-fill--policies"
                            style={{ width: `${(point.count / peakAcceptanceTrend) * 100}%` }}
                          />
                        </div>
                        <strong>{point.count}</strong>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="analytics-panel analytics-panel--wide">
                  <h3 className="analytics-panel-title">Non-compliant users</h3>
                  <p className="analytics-panel-desc">
                    Users who have not accepted every active policy flagged as requiring acceptance. Pending
                    count = required − accepted for that user.
                  </p>
                  <div className="rank-list">
                    {data.policies.nonCompliantUsers.length === 0 && (
                      <div className="empty-row">All users are compliant.</div>
                    )}
                    {data.policies.nonCompliantUsers.map((user) => (
                      <div className="rank-row" key={user.userId}>
                        <div>
                          <strong>{user.fullName}</strong>
                          <p>
                            {user.role} • {user.department}
                          </p>
                        </div>
                        <span>{user.pendingPolicies} pending</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* ── Notifications ── */}
            <section id="section-notifications" className="analytics-section">
              <header className="analytics-section-header">
                <h2 className="analytics-section-title">Notifications</h2>
              </header>
              <div className="analytics-cards analytics-cards--section">
                <div className="analytics-card">
                  <span>Total sent</span>
                  <strong>{formatNumber(data.notifications.totalNotificationsSent)}</strong>
                </div>
                <div className="analytics-card">
                  <span>Read rate</span>
                  <strong>{formatPercent(data.notifications.readVsUnreadRate.readRate)}</strong>
                </div>
                <div className="analytics-card">
                  <span>Unread rate</span>
                  <strong>{formatPercent(data.notifications.readVsUnreadRate.unreadRate)}</strong>
                </div>
              </div>
              <div className="analytics-grid analytics-grid--section analytics-grid--single">
                <div className="analytics-panel">
                  <h3 className="analytics-panel-title">Read vs unread</h3>
                  <p className="analytics-panel-desc">
                    Read rate = read rows ÷ all notification rows × 100. Unread rate is the complement (sums may
                    be 100% when only these two states exist).
                  </p>
                  <div className="policy-overview">
                    <div>
                      <span>Read</span>
                      <strong>
                        {data.notifications.readVsUnreadRate.readCount} (
                        {formatPercent(data.notifications.readVsUnreadRate.readRate)})
                      </strong>
                    </div>
                    <div>
                      <span>Unread</span>
                      <strong>
                        {data.notifications.readVsUnreadRate.unreadCount} (
                        {formatPercent(data.notifications.readVsUnreadRate.unreadRate)})
                      </strong>
                    </div>
                  </div>
                  <h3 className="analytics-panel-title analytics-panel-title--spaced">By type</h3>
                  <p className="analytics-panel-desc">
                    Inferred from which foreign key is set: POST (postId), EVENT (eventId), DOCUMENT
                    (documentId), else GENERAL.
                  </p>
                  <div className="notification-types">
                    {data.notifications.notificationsByType.map((item) => (
                      <div key={item.type} className="chip">
                        {item.type}: {item.count}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* ── Methodology ── */}
            <section id="section-methodology" className="analytics-section analytics-section--methodology">
              <header className="analytics-section-header">
                <h2 className="analytics-section-title">How metrics are calculated</h2>
                <p className="analytics-section-desc">
                  Short reference for how each area is derived from database tables. Percentages are rounded to
                  two decimal places unless noted.
                </p>
              </header>
              <div className="methodology-grid">
                <div className="methodology-card">
                  <h3>Users</h3>
                  <ul>
                    <li>
                      <strong>Total employees</strong> — <code>COUNT(*)</code> on the users table.
                    </li>
                    <li>
                      <strong>Profile completion rate</strong> — users with non-blank email{' '}
                      <em>and</em> non-blank profile picture ÷ total employees × 100.
                    </li>
                    <li>
                      <strong>Role distribution</strong> — group by <code>role</code>; each bar shows count ÷
                      total employees.
                    </li>
                  </ul>
                </div>
                <div className="methodology-card">
                  <h3>Engagement</h3>
                  <ul>
                    <li>
                      <strong>Posts by category / department</strong> — group posts; department blank →
                      UNSPECIFIED. Bar = category (or dept) count ÷ total posts × 100.
                    </li>
                    <li>
                      <strong>Most liked / commented</strong> — aggregate likes or comments per post; take top
                      10.
                    </li>
                    <li>
                      <strong>Most active users</strong> — for each user, posts created + likes given + comments
                      made; top 10 by sum.
                    </li>
                  </ul>
                </div>
                <div className="methodology-card">
                  <h3>Policies</h3>
                  <ul>
                    <li>
                      <strong>Acceptance rate (per policy)</strong> — users with an acceptance row for that
                      policy ÷ total employees × 100. Only policies that are active and require acceptance are
                      included in compliance logic.
                    </li>
                    <li>
                      <strong>Acceptance trend</strong> — count acceptances per month using{' '}
                      <code>acceptedAt</code>.
                    </li>
                    <li>
                      <strong>Non-compliant</strong> — users missing one or more required acceptances; pending =
                      required count − accepted count for required policies only.
                    </li>
                  </ul>
                </div>
                <div className="methodology-card">
                  <h3>Notifications</h3>
                  <ul>
                    <li>
                      <strong>Read / unread rates</strong> — count rows where <code>isRead</code> is true or
                      false ÷ total notification rows × 100.
                    </li>
                    <li>
                      <strong>Type</strong> — POST / EVENT / DOCUMENT if the corresponding id is set; otherwise
                      GENERAL.
                    </li>
                  </ul>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
